import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';

interface SearchBarProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  debounceMs?: number;
  isSearching?: boolean;
  onClear?: () => void;
  value?: string;
  onChange?: (value: string) => void;
}

export function SearchBar({
  onSearch,
  placeholder = 'Search books...',
  debounceMs = 300,
  isSearching = false,
  onClear,
  value: controlledValue,
  onChange,
}: SearchBarProps) {
  const [internalValue, setInternalValue] = useState('');
  const isFirstRender = useRef(true);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const setValue = useCallback(
    (newValue: string) => {
      if (isControlled) {
        onChange?.(newValue);
      } else {
        setInternalValue(newValue);
      }
    },
    [isControlled, onChange],
  );

  const debouncedValue = useDebounce(value, debounceMs);
  const isTyping = value !== debouncedValue;

  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onSearchRef.current(debouncedValue);
  }, [debouncedValue]);

  const handleClear = () => {
    setValue('');
    onClear?.();
  };

  const showSpinner = isTyping || isSearching;

  return (
    <div className="search-bar">
      <Search className="search-icon" aria-hidden="true" />
      <input
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className="search-input"
        aria-label="Search"
      />
      {showSpinner && (
        <Loader2 className="search-spinner animate-spin" aria-label="Searching..." />
      )}
      {value && !showSpinner && (
        <button
          type="button"
          onClick={handleClear}
          className="search-clear"
          aria-label="Clear search"
        >
          <X className="icon-sm" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
